import type {
    SurveyAnswerOptionResponseModel,
    SurveyQuestionResponseModel,
    SurveyQuestionViewModel,
    SurveyResponseViewModel
} from 'autogen/swagger/all';
import React, { type ReactNode, useCallback, useContext, useState } from 'react';
import type { useSurveyInstance } from '../shared/hooks/useSurveyInstance';
import { isInRange } from '../shared/helpers';

type SurveyResponseStateContext = {
    updateAnswerOption: (
        questionId: SurveyQuestionViewModel['id'],
        answerValue: SurveyAnswerOptionResponseModel
    ) => void;
    removeAnswerOption: (
        questionId: SurveyQuestionViewModel['id'],
        answerOptionId: SurveyAnswerOptionResponseModel['answerOptionId']
    ) => void;
    isAnswerSelected: (
        questionId: SurveyQuestionViewModel['id'],
        answerOptionId: SurveyAnswerOptionResponseModel['answerOptionId']
    ) => boolean;
    updateQuestionResponses: (newQuestionValue: SurveyQuestionResponseModel) => void;
    submitCurrentResponses: () => Promise<void>;
    updateQuestionResponsesAndSubmitResponses: (newQuestionValue: SurveyQuestionResponseModel) => Promise<void>;
    goToPreviousSurveyPage: ReturnType<typeof useSurveyInstance>['goToPreviousSurveyPage'];
    invalidItems: SurveyQuestionViewModel[];
};

const SurveyResponseStateContext = React.createContext<SurveyResponseStateContext>({} as SurveyResponseStateContext);

export default function SurveyResponseStateProvider({
    children,
    model,
    updateSurvey,
    goToPreviousSurveyPage
}: {
    children: ReactNode;
    model: SurveyResponseViewModel;
    updateSurvey: ReturnType<typeof useSurveyInstance>['updateSurvey'];
    goToPreviousSurveyPage: ReturnType<typeof useSurveyInstance>['goToPreviousSurveyPage'];
}) {
    const initializeSelectedQuestionResponsesState = (): SurveyQuestionResponseModel[] => {
        const InitialQuestionResponses: SurveyQuestionResponseModel[] = model.currentPage.questions.reduce(
            (result, currentResponse) => {
                const answerOptionResponses: SurveyAnswerOptionResponseModel[] = [];

                // Loop through the answers in the question to find if they are initially selected
                for (let index = 0; index < currentResponse.answerOptions.length; index++) {
                    const answer = currentResponse.answerOptions[index];

                    if (answer.selected) {
                        answerOptionResponses.push({
                            value: '',
                            answerOptionId: answer.id,
                            dynamicId: answer.dynamicData?.id || ''
                        });
                    }
                }

                // if there are selected answers we add them to the questionsResponse
                if (answerOptionResponses.length) {
                    const questionResponse: SurveyQuestionResponseModel = {
                        questionId: currentResponse.id,
                        isSelectAllChosen: false,
                        answerOptionResponses,
                        id: 0 // please don't ask why this is 0 i've only copied old logic that sent this as 0 for whatever reason.
                    };

                    result.push(questionResponse);
                }

                return result;
            },
            [] as SurveyQuestionResponseModel[]
        );

        return InitialQuestionResponses;
    };

    const [questionResponses, setQuestionResponses] = useState<SurveyQuestionResponseModel[]>(
        initializeSelectedQuestionResponsesState
    );
    const [invalidItems, setInvalidItems] = useState<SurveyQuestionViewModel[]>([]);

    /**
     * Check if an answer is selected
     * @param questionId
     * @param answerOptionId
     * @returns
     */
    const isAnswerSelected = (
        questionId: SurveyQuestionViewModel['id'],
        answerOptionId: SurveyAnswerOptionResponseModel['answerOptionId']
    ) => {
        const question = questionResponses.find((question) => question.questionId === questionId); // find the question that the answer belongs to
        const isSelected = question?.answerOptionResponses.find((answer) => answer.answerOptionId === answerOptionId); // If the answer exists it means it is selected
        return !!isSelected;
    };

    /**
     * Find question response
     * @param questionId
     * @returns
     */
    const findQuestionResponse = (questionId: SurveyQuestionViewModel['id']) =>
        questionResponses.find((question) => question.questionId === questionId);

    /**
     * Update an question response
     * @param newQuestionValue
     */
    const updateQuestionResponses = (newQuestionValue: SurveyQuestionResponseModel) => {
        const previousQuestions = questionResponses.filter(
            (question) => question.questionId !== newQuestionValue.questionId
        );
        const updatedQuestions = [...previousQuestions, newQuestionValue]; // update the questionResponses with the new questionResponse

        setQuestionResponses(updatedQuestions);

        return updatedQuestions;
    };

    /**
     * Update an answer on a question response
     * @param questionId
     * @param newAnswerValue
     * @returns
     */
    const updateAnswerOption = (
        questionId: SurveyQuestionViewModel['id'],
        newAnswerValue: SurveyAnswerOptionResponseModel
    ) => {
        const questionResponse = findQuestionResponse(questionId);

        if (!questionResponse)
            return updateQuestionResponses({
                questionId,
                isSelectAllChosen: false,
                id: 0,
                answerOptionResponses: [newAnswerValue]
            });

        // find all the previous answers in the question
        const previousAnswers = questionResponse.answerOptionResponses.filter(
            (answer) => answer.answerOptionId !== newAnswerValue.answerOptionId
        );

        // merge the new and previous answers
        const updatedAnswers = [...previousAnswers, newAnswerValue];

        const updatedQuestion: SurveyQuestionResponseModel = {
            ...questionResponse,
            answerOptionResponses: updatedAnswers
        };
        updateQuestionResponses(updatedQuestion);
    };

    /**
     * Remove an answer from a question response
     * @param questionId
     * @param answerOptionId
     * @returns
     */
    const removeAnswerOption = (
        questionId: SurveyQuestionViewModel['id'],
        answerOptionId: SurveyAnswerOptionResponseModel['answerOptionId']
    ) => {
        const questionResponse = findQuestionResponse(questionId);

        if (!questionResponse) return;

        // remove the answer by filtering
        const updatedAnswers = questionResponse.answerOptionResponses.filter(
            (answer) => answer.answerOptionId !== answerOptionId
        );

        const updatedQuestion: SurveyQuestionResponseModel = {
            ...questionResponse,
            answerOptionResponses: updatedAnswers
        };
        updateQuestionResponses(updatedQuestion);
    };

    /**
     * Clear all answers
     * @returns
     */
    const clearQuestionResponses = () => setQuestionResponses([]);

    const findInvalidItems = useCallback(
        (
            validationItems: SurveyQuestionViewModel[],
            responseItems: SurveyQuestionResponseModel[]
        ): SurveyQuestionViewModel[] =>
            validationItems.filter((question) => {
                const { id, minimumChoice, maximumChoice, answerOptions } = question;

                const response = responseItems.find(({ questionId }) => questionId === id);

                // return all the items with no response
                if (!response) return true;
                const { isSelectAllChosen, answerOptionResponses } = response;

                const numberOfResponses = isSelectAllChosen ? answerOptions.length : answerOptionResponses.length;

                const isPassingMinMaxCheck = isInRange(numberOfResponses, minimumChoice, maximumChoice);

                // return all the items that doesn't pass the min/max validation
                return !isPassingMinMaxCheck;
            }),
        []
    );

    /**
     * Method for checking validity of questionResponses
     * @param questionResponses
     * @returns
     */
    const checkResponseValidity = useCallback(
        (questionResponses: SurveyQuestionResponseModel[]): boolean => {
            const needsValidation = model.currentPage.questions.filter(
                ({ minimumChoice, maximumChoice }) => minimumChoice || maximumChoice
            );
            if (!needsValidation) return true;

            const invalid = findInvalidItems(needsValidation, questionResponses);

            if (!invalid.length) return true;

            setInvalidItems(invalid);

            document
                .querySelector(`[data-question-id="${invalidItems[0].id}"]`)
                ?.scrollIntoView({ behavior: 'smooth' });

            return false;
        },
        [findInvalidItems, invalidItems, model]
    );

    /**
     * Method for handling submitting questionResponses
     * @param questionResponses
     * @returns
     */
    const handleSubmitResponses = async (questionResponses: SurveyQuestionResponseModel[]): Promise<void> => {
        const isValid = checkResponseValidity(questionResponses);
        if (!isValid) return;

        await updateSurvey(questionResponses);

        clearQuestionResponses();
    };

    /**
     * Submit the current responses in the questionResponses state
     * @returns
     */
    const submitCurrentResponses = async () => handleSubmitResponses(questionResponses);

    /**
     *  This method only exist because setting a state is an asynchronous action that is batched by react. 
        calling updateQuestionResponses -> submitCurrentResponses right after each other would fail due to submitCurrentResponses not having the new state that updateQuestionResponses just updated.
        The solution is just to create a method where handleSubmitResponses has direct access to the questionResponses that were just updated, and not the questionResponses state handled by react.
     * @param newQuestionValue 
     */
    const updateQuestionResponsesAndSubmitResponses = async (newQuestionValue: SurveyQuestionResponseModel) => {
        const questionResponses = updateQuestionResponses(newQuestionValue);
        handleSubmitResponses(questionResponses);
    };

    const value = {
        invalidItems,
        submitCurrentResponses,
        updateQuestionResponsesAndSubmitResponses,
        isAnswerSelected,
        updateAnswerOption,
        removeAnswerOption,
        updateQuestionResponses,
        goToPreviousSurveyPage
    };

    return <SurveyResponseStateContext.Provider value={value}>{children}</SurveyResponseStateContext.Provider>;
}

export function useSurveyResponseState() {
    return useContext(SurveyResponseStateContext);
}
